import { removeAccents } from './filenameGenerator';
import type { Work } from '../types/work';

/** Partículas comuns em nomes lusófonos/hispânicos — nunca são o sobrenome final */
const PARTICLES = new Set(['da', 'de', 'do', 'das', 'dos', 'e', 'del', 'di', 'van', 'von']);

interface ParsedName {
  surname: string;
  initials: string[];
  givenNames: string[];
}

/**
 * Normaliza e parseia um nome acadêmico em sobrenome + iniciais/nomes.
 * Aceita formatos: "Silva, A.B.", "Silva da Costa, Adrian", "Adrian Barboza Silva da Costa"
 * Partículas (da, de, dos, e) são agregadas ao sobrenome.
 */
export function parseAcademicName(raw: string): ParsedName {
  const norm = removeAccents(raw.trim()).toLowerCase();
  if (!norm) return { surname: '', initials: [], givenNames: [] };

  let surname: string;
  let rest: string;

  if (norm.includes(',')) {
    // Formato "Sobrenome(s), Nome(s)/Iniciais"
    const [sur, ...parts] = norm.split(',');
    surname = sur.trim();
    rest = parts.join(',').trim();
  } else {
    // Formato "Nome(s) Sobrenome(s)" — extrair sobrenome composto com partículas
    const parts = norm.split(/\s+/);
    // Encontrar onde começa o sobrenome: primeira partícula seguida de não-partícula,
    // ou o último token se não houver partículas.
    let surnameStart = parts.length - 1;
    for (let i = parts.length - 2; i >= 1; i--) {
      if (PARTICLES.has(parts[i])) {
        surnameStart = i;
      } else {
        break;
      }
    }
    surname = parts.slice(surnameStart).join(' ');
    rest = parts.slice(0, surnameStart).join(' ');
  }

  // Extrair iniciais e nomes dados
  const tokens = rest.split(/[\s.]+/).filter(Boolean);
  const initials: string[] = [];
  const givenNames: string[] = [];

  for (const t of tokens) {
    if (t.length === 1) {
      initials.push(t);
    } else {
      givenNames.push(t);
      initials.push(t[0]);
    }
  }

  return { surname, initials, givenNames };
}

/**
 * Verifica se um autor da referência bate com o nome acadêmico do usuário.
 * Fuzzy mas conservador: sobrenome é âncora, iniciais são filtro secundário.
 */
/**
 * Constrói o sobrenome completo de um author, incluindo partículas.
 * Ex.: lastName="Costa", firstName="Adrian da" → tenta extrair "da costa" se
 * as partículas estiverem no firstName. Mas se lastName já é composto, usa direto.
 */
function buildAuthorSurname(lastName: string, firstName: string): string {
  // Se lastName já contém partículas (ex: "da Costa"), usar direto
  if (lastName.split(/\s+/).length > 1) return lastName;

  // Verificar se partículas estão no final do firstName (ex: "Adrian da" + "Costa")
  const firstTokens = firstName.split(/\s+/).filter(Boolean);
  const trailingParticles: string[] = [];
  for (let i = firstTokens.length - 1; i >= 0; i--) {
    if (PARTICLES.has(firstTokens[i])) {
      trailingParticles.unshift(firstTokens[i]);
    } else {
      break;
    }
  }

  if (trailingParticles.length > 0) {
    return [...trailingParticles, lastName].join(' ');
  }

  return lastName;
}

export function matchesAuthorName(
  authors: Work['authors'],
  parsed: ParsedName
): boolean {
  if (!parsed.surname || !authors?.length) return false;

  for (const author of authors) {
    const rawLastName = removeAccents(
      (author.lastName || author.family || '').trim()
    ).toLowerCase();
    const rawFirstName = removeAccents(
      (author.firstName || author.given || author.name || '').trim()
    ).toLowerCase();

    // Se só tem name (nome completo), tentar separar
    if (!rawLastName && !rawFirstName && author.name) {
      const fullNorm = removeAccents(author.name.trim()).toLowerCase();
      if (matchFullNameString(fullNorm, parsed)) return true;
      continue;
    }

    // Construir sobrenome com partículas
    const lastName = buildAuthorSurname(rawLastName, rawFirstName);

    // Sobrenome deve bater exatamente
    if (lastName !== parsed.surname) continue;

    // Se não temos iniciais do usuário:
    // - se o autor também não tem firstName → match (ambos são monônimos)
    // - se o autor TEM firstName → não é match seguro (evitar falso positivo)
    if (parsed.initials.length === 0) {
      if (!rawFirstName || rawFirstName.length === 0) return true;
      continue;
    }

    // Extrair iniciais (excluindo partículas que migraram para o sobrenome)
    const firstTokens = rawFirstName.split(/[\s.]+/).filter(Boolean);
    const givenTokens = firstTokens.filter(t => !PARTICLES.has(t) || t.length > 3);
    const authorInitials = givenTokens.map(t => t[0]);

    // Verificar se as iniciais do usuário batem (parcialmente OK)
    if (parsed.initials.length > 0 && authorInitials.length > 0) {
      if (authorInitials[0] === parsed.initials[0]) return true;
    }

    // Verificar nomes dados completos
    if (parsed.givenNames.length > 0 && givenTokens.length > 0) {
      if (parsed.givenNames.some(gn => givenTokens.some(at => at.startsWith(gn) || gn.startsWith(at)))) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Tenta casar uma string de nome completo (author.name) com o parsed name.
 */
function matchFullNameString(full: string, parsed: ParsedName): boolean {
  // Tentar formato "Sobrenome(s), Nome"
  if (full.includes(',')) {
    const [sur, rest] = full.split(',').map(s => s.trim());
    if (sur === parsed.surname) {
      if (!parsed.initials.length) return true;
      const tokens = rest.split(/[\s.]+/).filter(Boolean);
      if (tokens.length > 0 && tokens[0][0] === parsed.initials[0]) return true;
    }
    return false;
  }

  // Formato "Nome(s) [partícula(s)] Sobrenome" — extrair sobrenome composto
  const parts = full.split(/\s+/);
  let surnameStart = parts.length - 1;
  for (let i = parts.length - 2; i >= 1; i--) {
    if (PARTICLES.has(parts[i])) {
      surnameStart = i;
    } else {
      break;
    }
  }
  const fullSurname = parts.slice(surnameStart).join(' ');

  if (fullSurname === parsed.surname) {
    if (!parsed.initials.length) return true;
    if (surnameStart > 0 && parts[0][0] === parsed.initials[0]) return true;
  }

  return false;
}

export interface DetectionResult {
  updatedWorks: Work[];
  newDetections: Work[];
}

/**
 * Detecta publicações do usuário em uma lista de referências.
 * Retorna as referências que devem ser atualizadas e as novas detecções.
 */
export function detectMyPublications(
  works: Work[],
  academicName: string,
  alternativeNames: string[] = [],
  detectOrientations = false
): DetectionResult {
  if (!academicName || !academicName.trim()) {
    return { updatedWorks: [], newDetections: [] };
  }

  // Parse all names (primary + alternatives)
  const allNames = [academicName, ...alternativeNames].filter(n => n.trim());
  const parsedNames = allNames.map(parseAcademicName).filter(p => p.surname);
  if (parsedNames.length === 0) return { updatedWorks: [], newDetections: [] };

  const updatedWorks: Work[] = [];
  const newDetections: Work[] = [];

  for (const work of works) {
    // Pular rejeitadas e confirmadas
    if (work.myAuthorship === 'rejected' || work.myAuthorship === 'confirmed') continue;

    let matched = false;

    // Check authorship against all name variants
    for (const parsed of parsedNames) {
      if (matchesAuthorName(work.authors, parsed)) {
        matched = true;
        break;
      }
    }

    // Check orientation (advisor field) if enabled
    if (!matched && detectOrientations && work.advisor) {
      for (const parsed of parsedNames) {
        const advisorAsAuthors: Work['authors'] = [{
          firstName: '',
          lastName: '',
          name: work.advisor,
        }];
        if (matchesAuthorName(advisorAsAuthors, parsed)) {
          matched = true;
          break;
        }
      }
    }

    if (matched) {
      if (work.myAuthorship !== 'auto-detected') {
        newDetections.push(work);
      }
      updatedWorks.push(work);
    }
  }

  return { updatedWorks, newDetections };
}
